import { supabase } from '../utils/supabase';
import HelpArticleService from './helpArticleService';

/**
 * Service for generating personalized help recommendations based on user data and behavior
 */
export class HelpRecommendationService {
  
  /**
   * Generate help suggestions based on user profile and activity
   * @param {Object} userProfile - User profile data
   * @param {Object} userActivity - User activity data (optional)
   * @returns {Promise<Array>} Array of personalized help suggestions
   */
  static async getPersonalizedSuggestions(userProfile, userActivity = null) {
    try {
      const userRole = userProfile?.role || 'va';
      const isAdmin = userRole === 'admin' || userRole === 'moderator';

      // Fetch ALL published articles (not filtered by role)
      // Role filtering can still be applied at the UI level if desired
      const articlesResult = await HelpArticleService.getAllArticles({
        is_published: true
      });

      let baseSuggestions = [];

      if (articlesResult.success && articlesResult.data.length > 0) {
        // For admins/moderators, show all articles
        // For regular users, filter by their role
        const roleFilteredArticles = isAdmin 
          ? articlesResult.data
          : articlesResult.data.filter(article => {
              const helpfulFor = article.helpful_for || [];
              return helpfulFor.includes(userRole) || helpfulFor.includes('both');
            });

        // Use database articles as base suggestions
        baseSuggestions = roleFilteredArticles.map(article => ({
          id: article.id,
          title: article.title,
          description: article.description,
          category: article.category,
          urgency: article.urgency,
          icon: article.icon_name,
          estimatedTime: article.estimated_time,
          tags: article.tags || [],
          steps: article.steps || [],
          content: article.content,
          slug: article.slug,
          view_count: article.view_count || 0,
          helpful_count: article.helpful_count || 0,
          helpful_for: article.helpful_for
        }));
      } else {
        // Fallback to hardcoded suggestions if database is empty
        baseSuggestions = this.getBaseSuggestions(userRole);
      }

      // Analyze profile completeness and prioritize suggestions
      const profileAnalysis = this.analyzeProfile(userProfile);
      const prioritizedSuggestions = this.prioritizeSuggestions(baseSuggestions, profileAnalysis, userActivity);

      return {
        success: true,
        data: prioritizedSuggestions,
        profileScore: profileAnalysis.completenessScore
      };
    } catch (error) {
      console.error('[Help Recommendations] Error generating suggestions:', error);
      // On error, return fallback suggestions
      return {
        success: false,
        error: error.message,
        data: this.getFallbackSuggestions(userProfile?.role || 'va')
      };
    }
  }

  /**
   * Get context-aware suggestions based on current user behavior
   * @param {Object} userProfile - User profile data
   * @param {string} currentContext - Current page/context user is in
   * @returns {Array} Context-aware suggestions
   */
  static getContextualSuggestions(userProfile, currentContext) {
    const userRole = userProfile?.role || 'va';
    const contextSuggestions = [];

    // Context-based suggestions
    switch (currentContext) {
      case 'profile-incomplete':
        contextSuggestions.push({
          id: 'complete-profile-urgent',
          title: 'Complete Your Profile Now',
          description: 'Your profile is incomplete. Complete it to get better job matches.',
          urgency: 'high',
          icon: 'AlertCircle',
          category: 'getting-started',
          estimatedTime: '10 min',
          actionUrl: userRole === 'va' ? '/va-profile-settings' : '/employer-profile-settings'
        });
        break;

      case 'low-matches':
        if (userRole === 'va') {
          contextSuggestions.push({
            id: 'improve-matching',
            title: 'Improve Your Job Matches',
            description: 'Learn how to optimize your profile for better job recommendations.',
            urgency: 'high',
            icon: 'Target',
            category: 'profile',
            estimatedTime: '15 min'
          });
        }
        break;

      case 'application-rejected':
        if (userRole === 'va') {
          contextSuggestions.push({
            id: 'application-tips',
            title: 'Improve Your Application Strategy',
            description: 'Learn proven techniques to write winning job applications.',
            urgency: 'high',
            icon: 'FileText',
            category: 'jobs',
            estimatedTime: '20 min'
          });
        }
        break;

      case 'payment-issue':
        contextSuggestions.push({
          id: 'payment-troubleshoot',
          title: 'Resolve Payment Issues',
          description: 'Step-by-step guide to fix common payment problems.',
          urgency: 'high',
          icon: 'CreditCard',
          category: 'payment',
          estimatedTime: '10 min'
        });
        break;
    }

    return contextSuggestions;
  }

  /**
   * Get trending help topics based on common user issues
   * @returns {Promise<Array>} Trending help topics
   */
  static async getTrendingTopics() {
    try {
      // In a real implementation, this would query analytics data
      const trendingTopics = [
        {
          id: 'password-reset',
          title: 'Reset Your Password',
          description: 'Having trouble logging in? Learn how to reset your password.',
          trend: 'up',
          icon: 'Lock',
          category: 'technical',
          helpfulCount: 342
        },
        {
          id: 'skill-verification',
          title: 'Verify Your Skills',
          description: 'Boost your credibility with verified skill badges.',
          trend: 'up',
          icon: 'Award',
          category: 'profile',
          helpfulCount: 287
        },
        {
          id: 'interview-prep',
          title: 'Prepare for Interviews',
          description: 'Tips and tricks to ace your virtual assistant interviews.',
          trend: 'stable',
          icon: 'Video',
          category: 'jobs',
          helpfulCount: 234
        }
      ];

      return {
        success: true,
        data: trendingTopics
      };
    } catch (error) {
      console.error('[Help Recommendations] Error fetching trending topics:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  /**
   * Log help topic interaction for analytics
   * @param {string} topicId - Help topic ID
   * @param {string} action - Action taken (viewed, helpful, not_helpful)
   * @param {Object} userProfile - User profile data
   */
  static async logInteraction(topicId, action, userProfile) {
    try {
      const { error } = await supabase
        .from('help_interactions')
        .insert({
          user_id: userProfile.id,
          topic_id: topicId,
          action: action,
          user_role: userProfile.role,
          timestamp: new Date().toISOString()
        });

      if (error) {
        console.error('[Help Recommendations] Error logging interaction:', error);
      }
    } catch (error) {
      console.error('[Help Recommendations] Error logging interaction:', error);
    }
  }

  /**
   * Get base suggestions for a user role
   * @private
   */
  static getBaseSuggestions(userRole) {
    const common = [
      {
        id: 'getting-started',
        title: 'Getting Started Guide',
        description: 'New to VA Sprouts? Start here for a complete walkthrough.',
        category: 'getting-started',
        urgency: 'medium',
        icon: 'Play',
        estimatedTime: '15 min',
        tags: ['onboarding', 'basics', 'guide']
      },
      {
        id: 'platform-guidelines',
        title: 'Platform Guidelines',
        description: 'Important rules and best practices for using VA Sprouts.',
        category: 'policies',
        urgency: 'low',
        icon: 'BookOpen',
        estimatedTime: '12 min',
        tags: ['guidelines', 'rules', 'policies']
      },
      {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Need personal assistance? Reach out to our support team.',
        category: 'technical',
        urgency: 'low',
        icon: 'MessageCircle',
        estimatedTime: '2 min',
        tags: ['support', 'contact', 'help']
      }
    ];

    const vaSuggestions = [
      {
        id: 'complete-profile',
        title: 'Complete Your VA Profile',
        description: 'Create a compelling profile that attracts top employers.',
        category: 'getting-started',
        urgency: 'high',
        icon: 'User',
        estimatedTime: '20 min',
        tags: ['profile', 'optimization', 'visibility']
      },
      {
        id: 'write-applications',
        title: 'Write Winning Applications',
        description: 'Learn how to craft applications that get noticed.',
        category: 'jobs',
        urgency: 'high',
        icon: 'PenTool',
        estimatedTime: '25 min',
        tags: ['applications', 'writing', 'success']
      },
      {
        id: 'build-portfolio',
        title: 'Build Your Portfolio',
        description: 'Showcase your work with an impressive portfolio.',
        category: 'profile',
        urgency: 'medium',
        icon: 'Briefcase',
        estimatedTime: '30 min',
        tags: ['portfolio', 'showcase', 'work-samples']
      },
      {
        id: 'skill-assessments',
        title: 'Take Skill Assessments',
        description: 'Verify your skills and boost your credibility.',
        category: 'profile',
        urgency: 'medium',
        icon: 'Award',
        estimatedTime: '15 min',
        tags: ['skills', 'verification', 'credibility']
      }
    ];

    const employerSuggestions = [
      {
        id: 'post-first-job',
        title: 'Post Your First Job',
        description: 'Learn how to create effective job postings that attract top VAs.',
        category: 'getting-started',
        urgency: 'high',
        icon: 'Plus',
        estimatedTime: '15 min',
        tags: ['job-posting', 'hiring', 'onboarding']
      },
      {
        id: 'screen-candidates',
        title: 'Screen Candidates Effectively',
        description: 'Best practices for evaluating virtual assistant applications.',
        category: 'jobs',
        urgency: 'high',
        icon: 'Filter',
        estimatedTime: '20 min',
        tags: ['screening', 'evaluation', 'hiring']
      },
      {
        id: 'manage-team',
        title: 'Manage Your VA Team',
        description: 'Tips for effectively managing remote virtual assistants.',
        category: 'messaging',
        urgency: 'medium',
        icon: 'Users',
        estimatedTime: '18 min',
        tags: ['management', 'team', 'productivity']
      },
      {
        id: 'optimize-budget',
        title: 'Optimize Your Hiring Budget',
        description: 'Get the most value from your virtual assistant investments.',
        category: 'payment',
        urgency: 'medium',
        icon: 'DollarSign',
        estimatedTime: '12 min',
        tags: ['budget', 'value', 'roi']
      }
    ];

    return userRole === 'va' ? [...common, ...vaSuggestions] : [...common, ...employerSuggestions];
  }

  /**
   * Analyze user profile for completeness and issues
   * @private
   */
  static analyzeProfile(userProfile) {
    const analysis = {
      completenessScore: 0,
      issues: [],
      strengths: []
    };

    if (!userProfile) {
      return analysis;
    }

    const userRole = userProfile.role || 'va';
    let totalFields = 0;
    let completedFields = 0;

    if (userRole === 'va') {
      const requiredFields = [
        'full_name', 'bio', 'skills', 'experience_years',
        'hourly_rate', 'location', 'profile_picture'
      ];

      totalFields = requiredFields.length;
      
      requiredFields.forEach(field => {
        if (userProfile[field]) {
          completedFields++;
        } else {
          analysis.issues.push(`Missing ${field.replace('_', ' ')}`);
        }
      });

      // Check skills array length
      if (userProfile.skills && userProfile.skills.length >= 5) {
        analysis.strengths.push('Good skill diversity');
      } else if (userProfile.skills && userProfile.skills.length > 0) {
        analysis.issues.push('Consider adding more skills');
      }

    } else if (userRole === 'employer') {
      const requiredFields = [
        'full_name', 'company_name', 'bio', 'location'
      ];

      totalFields = requiredFields.length;
      
      requiredFields.forEach(field => {
        if (userProfile[field]) {
          completedFields++;
        } else {
          analysis.issues.push(`Missing ${field.replace('_', ' ')}`);
        }
      });
    }

    analysis.completenessScore = Math.round((completedFields / totalFields) * 100);
    return analysis;
  }

  /**
   * Prioritize suggestions based on profile analysis
   * @private
   */
  static prioritizeSuggestions(baseSuggestions, profileAnalysis, userActivity) {
    const prioritized = [...baseSuggestions];

    // Boost urgency for profile completion if score is low
    if (profileAnalysis.completenessScore < 70) {
      const profileSuggestion = prioritized.find(s => s.id === 'complete-profile');
      if (profileSuggestion) {
        profileSuggestion.urgency = 'high';
        profileSuggestion.personalized = true;
        profileSuggestion.reason = `Profile ${profileAnalysis.completenessScore}% complete`;
      }
    }

    // Add activity-based prioritization
    if (userActivity) {
      if (userActivity.lowApplicationSuccess) {
        const appSuggestion = prioritized.find(s => s.id === 'write-applications');
        if (appSuggestion) {
          appSuggestion.urgency = 'high';
          appSuggestion.personalized = true;
          appSuggestion.reason = 'Low application success rate detected';
        }
      }

      if (userActivity.noActiveJobs && userActivity.userRole === 'employer') {
        const jobSuggestion = prioritized.find(s => s.id === 'post-first-job');
        if (jobSuggestion) {
          jobSuggestion.urgency = 'high';
          jobSuggestion.personalized = true;
          jobSuggestion.reason = 'Start hiring your first VA';
        }
      }
    }

    // Sort by urgency and personalization
    return prioritized.sort((a, b) => {
      if (a.personalized && !b.personalized) return -1;
      if (!a.personalized && b.personalized) return 1;
      
      const urgencyOrder = { high: 3, medium: 2, low: 1 };
      return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
    });
  }

  /**
   * Get fallback suggestions when personalization fails
   * @private
   */
  static getFallbackSuggestions(_userRole) {
    return [
      {
        id: 'general-help',
        title: 'General Help',
        description: 'Browse our comprehensive help documentation.',
        category: 'getting-started',
        urgency: 'medium',
        icon: 'Book',
        estimatedTime: '5 min'
      },
      {
        id: 'contact-support-fallback',
        title: 'Contact Support',
        description: 'Get personalized help from our support team.',
        category: 'technical',
        urgency: 'low',
        icon: 'MessageCircle',
        estimatedTime: '2 min'
      }
    ];
  }
}

export default HelpRecommendationService;
